/**
 * Video Overlay Handler with AbortError Prevention
 * 
 * This module addresses the AbortError issue happening in Safari 18.x when users click video overlays.
 * The error occurs due to race conditions between multiple network requests firing simultaneously.
 */

class VideoOverlayHandler {
    constructor() {
        this.clickTimeouts = new Map();
        this.lastClickTime = 0;
        this.debounceDelay = 300; // 300ms debounce
    }

    /**
     * Enhanced overlay click handler with debouncing
     * @param {HTMLElement} overlay - The overlay element
     * @param {Function} originalHandler - Original click handler function
     * @param {Object} context - Context for the handler (usually 'this')
     */
    createDebouncedHandler(overlay, originalHandler, context = null) {
        const overlayId = this.getOverlayId(overlay);

        return (event) => {
            const now = Date.now();

            // Clear existing timeout for this overlay
            if (this.clickTimeouts.has(overlayId)) {
                clearTimeout(this.clickTimeouts.get(overlayId));
            }

            // Prevent rapid clicking
            if (now - this.lastClickTime < this.debounceDelay) {
                if (window.Sentry) {
                    window.Sentry.addBreadcrumb({
                        message: 'Video overlay click debounced',
                        level: 'info',
                        data: {
                            overlay_id: overlayId,
                            time_since_last_click: now - this.lastClickTime,
                            user_agent: navigator.userAgent,
                        }
                    });
                }
                return;
            }

            this.lastClickTime = now;

            // Add error boundary around the original handler
            const timeout = setTimeout(() => {
                try {
                    if (context) {
                        originalHandler.call(context, event);
                    } else {
                        originalHandler(event);
                    }

                    // Log successful overlay click for debugging
                    if (window.Sentry) {
                        window.Sentry.addBreadcrumb({
                            message: 'Video overlay clicked successfully',
                            level: 'info',
                            data: {
                                overlay_id: overlayId,
                                user_agent: navigator.userAgent,
                                timestamp: now,
                            }
                        });
                    }
                } catch (error) {
                    this.handleOverlayError(error, overlayId);
                }

                this.clickTimeouts.delete(overlayId);
            }, 50); // Small delay to allow other scripts to settle

            this.clickTimeouts.set(overlayId, timeout);
        };
    }

    /**
     * Handle errors that occur during overlay click handling
     * @param {Error} error - The error that occurred
     * @param {string} overlayId - The overlay identifier
     */
    handleOverlayError(error, overlayId) {
        if (error.name === 'AbortError') {
            console.log('Video overlay operation was aborted (this is usually not an error)');
            if (window.Sentry) {
                window.Sentry.addBreadcrumb({
                    message: 'Video overlay operation aborted',
                    level: 'info',
                    data: {
                        overlay_id: overlayId,
                        error_name: error.name,
                        error_message: error.message,
                        user_agent: navigator.userAgent,
                    }
                });
            }
        } else {
            console.error('Video overlay error:', error);
            if (window.Sentry) {
                window.Sentry.captureException(error, {
                    tags: {
                        component: 'video_overlay_handler',
                        overlay_id: overlayId,
                    },
                    extra: {
                        user_agent: navigator.userAgent,
                        timestamp: Date.now(),
                    }
                });
            }
        }
    }

    /**
     * Get a unique identifier for an overlay element
     * @param {HTMLElement} overlay - The overlay element
     * @returns {string} - Unique identifier
     */
    getOverlayId(overlay) {
        if (overlay.id) {
            return overlay.id;
        }

        // Check if we already have a cached ID
        if (overlay.dataset.overlayId) {
            return overlay.dataset.overlayId;
        }

        // Create an ID based on element characteristics that don't require layout calculation
        const classes = Array.from(overlay.classList).join('-');
        const tagName = overlay.tagName.toLowerCase();
        const index = Array.from(overlay.parentNode?.children || []).indexOf(overlay);

        // Use a combination of tag, classes, and DOM position for uniqueness
        const generatedId = `overlay-${tagName}-${classes}-${index}-${Date.now()}`;

        // Cache the ID as a data attribute to avoid regeneration
        overlay.dataset.overlayId = generatedId;

        return generatedId;
    }

    /**
     * Wrap window.ipse.dispatch calls to handle AbortErrors
     * @param {Object} eventData - The event data to dispatch
     */
    safeIpseDispatch(eventData) {
        if (!window.ipse || typeof window.ipse.dispatch !== 'function') {
            console.warn('window.ipse.dispatch not available');
            return;
        }

        try {
            return window.ipse.dispatch(eventData);
        } catch (error) {
            if (error.name === 'AbortError') {
                console.log('Ipse dispatch was aborted (this is usually not an error)');
                if (window.Sentry) {
                    window.Sentry.addBreadcrumb({
                        message: 'Ipse dispatch aborted',
                        level: 'info',
                        data: {
                            event_action: eventData.action,
                            error_name: error.name,
                            user_agent: navigator.userAgent,
                        }
                    });
                }
            } else {
                console.error('Ipse dispatch error:', error);
                if (window.Sentry) {
                    window.Sentry.captureException(error, {
                        tags: {
                            component: 'ipse_dispatch'
                        },
                        extra: {
                            event_data: eventData,
                        }
                    });
                }
            }
        }
    }

    /**
     * Global error handler for unhandled promise rejections
     */
    setupGlobalErrorHandling() {
        console.log('VideoOverlayHandler: Setting up global error handling for AbortErrors');

        // Handle unhandled promise rejections (including AbortError)
        window.addEventListener('unhandledrejection', (event) => {
            if (event.reason && event.reason.name === 'AbortError') {
                console.log('Unhandled AbortError caught and handled:', event.reason.message);

                if (window.Sentry) {
                    window.Sentry.addBreadcrumb({
                        message: 'Unhandled AbortError caught by VideoOverlayHandler',
                        level: 'info',
                        data: {
                            error_name: event.reason.name,
                            error_message: event.reason.message,
                            user_agent: navigator.userAgent,
                            stack: event.reason.stack,
                            handler: 'VideoOverlayHandler',
                        },
                    });
                }

                // Prevent the error from being logged as unhandled
                event.preventDefault();
            }
        });

        // Catch regular error events that might contain AbortErrors
        window.addEventListener('error', (event) => {
            if (event.error && event.error.name === 'AbortError') {
                console.log('VideoOverlayHandler: AbortError caught in error event:', event.error.message);

                if (window.Sentry) {
                    window.Sentry.addBreadcrumb({
                        message: 'AbortError caught in error event by VideoOverlayHandler',
                        level: 'info',
                        data: {
                            error_name: event.error.name,
                            error_message: event.error.message,
                            user_agent: navigator.userAgent,
                            stack: event.error.stack,
                            handler: 'VideoOverlayHandler',
                        },
                    });
                }

                // Prevent default error handling
                event.preventDefault();
            }
        });

        console.log('VideoOverlayHandler: Global error handling setup complete');
    }
}

// Create global instance
window.VideoOverlayHandler = new VideoOverlayHandler();

// Set up global error handling immediately
window.VideoOverlayHandler.setupGlobalErrorHandling();

export default window.VideoOverlayHandler; 